import React from 'react'
import { Chip, Stack, Tooltip, darken, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IPositionWithDrawing } from 'src/generated-types'
import { IChangeType, IPositionChange } from 'src/generated-types/project-version-changes'
import { GetRGBA } from 'src/utils'
import { CHANGE_GLYPH } from './changeLabels'
import { describeChange, getChangeColor, Translate } from './projectVersionChanges.action'
import { useRevisionChanges } from './RevisionChangesContext'

const MAX_CHIPS = 2

interface PositionRevisionMarkersProps {
  change?: IPositionChange
  // Set on a drawing card, which summarises its positions instead of describing one change.
  groupedPositions?: IPositionWithDrawing[]
}

// Change chips on a position row of a draft, e.g. "Moved 14 m SW" or "Shackle -> Heavy shackle".
export const PositionRevisionMarkers: React.FC<PositionRevisionMarkersProps> = ({ change, groupedPositions }) => {
  const { t: translate } = useTranslation(['projects'])
  const theme = useTheme()
  const { isRevision, changeByName } = useRevisionChanges()

  if (!isRevision) return null

  if (groupedPositions) {
    const changed = groupedPositions.filter(p => changeByName.has(p.name ?? '')).length
    return changed > 0 ? (
      <Chip size="small" label={translate('revision-n-changed', { ns: 'projects', count: changed })} />
    ) : null
  }

  if (!change) return null

  const t: Translate = (key, options) => translate(key, { ns: 'projects', ...options }) as string
  const sentences = describeChange(change, t)
  const shown = sentences.slice(0, MAX_CHIPS)
  const hidden = sentences.length - shown.length
  const kindOf = (index: number) => change.kinds[index] ?? change.kinds[0]

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{sentences.join('\n')}</span>}>
      <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
        {shown.map((text, index) => {
          const kind = kindOf(index)
          const color = getChangeColor(theme, kind)
          return (
            <KindTag
              key={text}
              tint={GetRGBA(color, 0.14)}
              ink={kind === IChangeType.Modified ? darken(color, 0.3) : color}
            >
              <span aria-hidden>{CHANGE_GLYPH[kind]}</span>
              {text}
            </KindTag>
          )
        })}
        {hidden > 0 && <Chip size="small" label={`+${hidden}`} />}
      </Stack>
    </Tooltip>
  )
}

export default PositionRevisionMarkers

export const KindTag = styled('span', {
  shouldForwardProp: prop => prop !== 'tint' && prop !== 'ink',
})<{ tint: string; ink: string }>(({ tint, ink }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 7px',
  borderRadius: 3,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  maxWidth: 300,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  backgroundColor: tint,
  color: ink,
}))
